import React, { useEffect, useMemo, useState } from 'react';
import { Container, Button } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import styles from '../components/bodywon.module.css';
import { normalizeAddress } from './addressHelpers';
import DeliveryAddressCard from './DeliveryAddressCard';
import { fetchAddressById } from '../utils/addressApi';
import { confirmOrderApi, fetchCartDetailById, ensureLoggedInUserId, isCartUserLoggedIn } from '../utils/cartApi';
import { useCart } from '../context/CartContext';
import { useSettings } from '../context/SettingsContext';
import { getImageSrc } from '../utils/productStorefront';
import { calculateLineTotal, calculateOrderTotals } from '../utils/cartTotals';
import OrderTotalsSummary from '../components/OrderTotalsSummary';

const OrderReviewPage = ({ onRequireLogin }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const cartId = searchParams.get('cartId');
  const addressId = searchParams.get('addressId');
  const { formatPrice, settings } = useSettings();
  const { refreshCart } = useCart();

  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState('');
  const [address, setAddress] = useState(null);
  const [cart, setCart] = useState(null);

  useEffect(() => {
    if (!isCartUserLoggedIn()) {
      onRequireLogin?.();
      navigate('/', { replace: true });
      return;
    }
    if (!cartId || !addressId) {
      setError('Missing cart or address. Please complete checkout again.');
      setLoading(false);
      return;
    }

    let active = true;

    (async () => {
      try {
        const userId = await ensureLoggedInUserId();
        const [addressData, cartData] = await Promise.all([
          fetchAddressById(addressId),
          fetchCartDetailById(cartId, userId),
        ]);
        if (!active) return;
        setAddress(normalizeAddress(addressData));
        setCart(cartData);
      } catch (err) {
        if (active) setError(err.message || 'Could not load order details.');
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [addressId, cartId, navigate, onRequireLogin]);

  const items = cart?.items ?? [];
  const totals = useMemo(
    () => calculateOrderTotals(items, settings, address),
    [items, settings, address]
  );

  const handleConfirm = async () => {
    const userId = await ensureLoggedInUserId();
    if (!userId) {
      onRequireLogin?.();
      return;
    }

    setConfirming(true);
    try {
      const result = await confirmOrderApi({
        cartId: Number(cartId),
        userId,
        addressId: Number(addressId),
        subTotal: totals.subtotal,
        discountAmount: totals.bundleDiscount,
        shippingAmount: 0,
        taxAmount: totals.taxAmount,
        totalAmount: totals.grandTotal,
        orderedBy: userId,
      });
      toast.success('Order confirmed!');
      await refreshCart({ showLoading: false });
      const orderQuery = result.orderId ? `&orderId=${result.orderId}` : '';
      const orderNumQuery = result.orderNumber ? `&orderNumber=${encodeURIComponent(result.orderNumber)}` : '';
      navigate(`/checkout/confirm?cartId=${cartId}&addressId=${addressId}${orderQuery}${orderNumQuery}`);
    } catch (err) {
      toast.error(err.message || 'Could not confirm order.');
    } finally {
      setConfirming(false);
    }
  };

  if (!isCartUserLoggedIn()) return null;

  return (
    <section className={styles.checkoutAddressPage}>
      <Container>
        <div className={styles.checkoutAddressHeader}>
          <h1 className={styles.checkoutAddressTitle}>Review Your Order</h1>
          <p className={styles.checkoutAddressSub}>Check products, amount and delivery address before confirming.</p>
        </div>

        {loading ? (
          <div className={styles.checkoutAddressCard}>Loading order details...</div>
        ) : error ? (
          <div className={styles.checkoutAddressCard}>
            <div className={styles.checkoutAddressError}>{error}</div>
            <Button variant="dark" onClick={() => navigate('/cart')}>Back to Cart</Button>
          </div>
        ) : (
          <div className={styles.orderReviewLayout}>
            <DeliveryAddressCard
              address={address}
              onEdit={() => navigate(`/checkout/address?cartId=${cartId}&addressId=${addressId}`)}
              editLabel="Edit"
            />

            <div className={styles.checkoutAddressCard}>
              <h2 className={styles.orderSectionTitle}>Products</h2>
              <div className={styles.orderItemsList}>
                {items.map((item) => {
                  const imageUrl = item.productImage ? getImageSrc(item.productImage) : '';
                  return (
                    <div key={item.cartItemId ?? item.productId} className={styles.orderItemRow}>
                      <div className={styles.orderItemImage}>
                        {imageUrl ? <img src={imageUrl} alt={item.productName} /> : null}
                      </div>
                      <div className={styles.orderItemInfo}>
                        <p className={styles.orderItemName}>{item.productName}</p>
                        <p className={styles.orderItemMeta}>
                          Qty: {item.quantity} × {formatPrice(item.price)}
                        </p>
                      </div>
                      <div className={styles.orderItemTotal}>{formatPrice(calculateLineTotal(item))}</div>
                    </div>
                  );
                })}
              </div>

              <OrderTotalsSummary totals={totals} formatPrice={formatPrice} />

              <div className={styles.checkoutAddressActions}>
                <Button variant="dark" onClick={handleConfirm} disabled={confirming}>
                  {confirming ? 'Confirming...' : 'Confirm Order'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </Container>
    </section>
  );
};

export default OrderReviewPage;
